/**
 * historyStore — Undo/Redo para o MorphERD
 *
 * ─── DECISÃO DE IMPLEMENTAÇÃO ───────────────────────────────────────────────
 * Store manual separado (não zundo middleware).
 *
 * Motivo: zundo observa todo setState de um único store e cria entradas
 * automaticamente — isso geraria histórico excessivo durante drag (per-pixel)
 * e não captura estado de múltiplos stores. O controle manual permite chamar
 * commitHistory() apenas em ações relevantes, após a mutação estar completa.
 *
 * Sem dependência circular: historyStore não importa nenhum outro store.
 * - commitHistory(snapshot, label?) recebe o snapshot pronto (diagramStore
 *   monta e passa o snapshot após cada mutação relevante).
 * - undo() / redo() retornam DiagramSnapshot | null; o chamador aplica ao
 *   diagramStore via useDiagramStore.setState(snapshot).
 *
 * ─── O QUE ENTRA NO SNAPSHOT ────────────────────────────────────────────────
 *   tables       — tabelas do diagrama (colunas, indexes, posição)
 *   rels         — relacionamentos (incluindo FK automática)
 *   groups       — agrupamentos de cor
 *   projectName  — nome do projeto (editável)
 *
 * ─── O QUE NÃO ENTRA NO SNAPSHOT ────────────────────────────────────────────
 *   viewport (sc, tx, ty)          — estado de sessão; desfazer zoom confunde
 *   selection (selectedTableId…)   — estado efêmero de UI
 *   settingsStore.defaults         — templates de coluna (não mudam tabelas já criadas)
 *   settingsStore.theme / grid     — preferências visuais, não são edições
 *   settingsStore.language         — preferência de idioma
 *
 * ─── DRAG E TRANSAÇÕES ──────────────────────────────────────────────────────
 * Canvas drag (T-21 / T-24) NÃO deve chamar commitHistory() a cada mousemove.
 * Padrão esperado para os futuros hooks de drag:
 *
 *   onPointerDown: useHistoryStore.getState().beginTransaction('move-table')
 *   onPointerMove: (apenas atualiza posição visual — sem commitHistory)
 *   onPointerUp:   useHistoryStore.getState().endTransaction()
 *
 * beginTransaction() suspende commits; endTransaction() comita o estado final.
 * Enquanto o canvas não está migrado, ambos são no-op.
 */

import { create } from 'zustand'
import type { Table, Relation, Groups, DatabaseDialect, ColumnDefaults } from '@/types'

// ── Snapshot ──────────────────────────────────────────────────────────────────

export interface DiagramSnapshot {
  tables: Table[]
  rels: Relation[]
  groups: Groups
  defaults: ColumnDefaults
  projectName: string
  dialect?: DatabaseDialect
  hiddenTableIds?: string[]
}

interface HistoryEntry {
  label: string
  snapshot: DiagramSnapshot
}

// ── Store interface ───────────────────────────────────────────────────────────

export interface HistoryState {
  undoStack: HistoryEntry[]
  redoStack: HistoryEntry[]

  /** true durante a aplicação de undo/redo — bloqueia novos commits */
  isRestoring: boolean

  /** máximo de entradas no undoStack (default: 20) */
  historyLimit: number

  /**
   * Registra um snapshot como ponto de histórico.
   * Chame APÓS a mutação ter sido aplicada ao diagramStore, passando o estado
   * resultante como snapshot. O store faz deepClone internamente.
   *
   * No-op se isRestoring = true (previne loop durante undo/redo).
   * No-op se o snapshot for idêntico ao último (deduplicação).
   * Limpa redoStack ao registrar nova ação.
   */
  commitHistory: (snapshot: DiagramSnapshot, label?: string) => void

  /**
   * Desfaz a última ação.
   * Retorna o snapshot a restaurar, ou null se não há nada para desfazer.
   * O chamador deve aplicar o snapshot ao diagramStore:
   *   const snap = useHistoryStore.getState().undo()
   *   if (snap) useDiagramStore.setState(snap)
   */
  undo: () => DiagramSnapshot | null

  /**
   * Refaz a última ação desfeita.
   * Retorna o snapshot a restaurar, ou null se não há nada para refazer.
   */
  redo: () => DiagramSnapshot | null

  /** Limpa toda a pilha. Use ao carregar um projeto do zero. */
  clearHistory: () => void

  /**
   * Inicializa o histórico com um snapshot base.
   * Chame uma vez após o app carregar o estado inicial (App.tsx ou init effect).
   * Garante que sempre exista pelo menos 1 entrada, permitindo undo após a
   * primeira ação do usuário.
   */
  initHistory: (snapshot: DiagramSnapshot) => void

  // ── Drag transaction stubs (T-21 / T-24) ────────────────────────────────

  /** Inicia transação: futuro suspensor de commitHistory durante drag. No-op até T-21. */
  beginTransaction: (label?: string) => void

  /** Encerra transação: futuramente comita estado ao soltar. No-op até T-21. */
  endTransaction: () => void

  // ── Selectors ────────────────────────────────────────────────────────────

  /** Retorna true se há ações para desfazer */
  canUndo: () => boolean

  /** Retorna true se há ações para refazer */
  canRedo: () => boolean
}

// ── Utilitário de clone profundo ──────────────────────────────────────────────

function deepClone<T>(obj: T): T {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj)
  }
  return JSON.parse(JSON.stringify(obj)) as T
}

// ── Implementação ─────────────────────────────────────────────────────────────

export const useHistoryStore = create<HistoryState>()((set, get) => ({
  undoStack: [],
  redoStack: [],
  isRestoring: false,
  historyLimit: 20,

  // ── commitHistory ─────────────────────────────────────────────────────────

  commitHistory: (snapshot, label = '') => {
    if (get().isRestoring) return

    const { undoStack, historyLimit } = get()

    // Dedup: não registra se idêntico ao último snapshot
    const lastEntry = undoStack[undoStack.length - 1]
    if (lastEntry) {
      const lastSerial = JSON.stringify(lastEntry.snapshot)
      const newSerial = JSON.stringify(snapshot)
      if (lastSerial === newSerial) return
    }

    const entry: HistoryEntry = { label, snapshot: deepClone(snapshot) }
    let newStack = [...undoStack, entry]

    // Enforça limite: mantém apenas os mais recentes
    if (newStack.length > historyLimit) {
      newStack = newStack.slice(newStack.length - historyLimit)
    }

    // Nova ação sempre limpa redoStack
    set({ undoStack: newStack, redoStack: [] })
  },

  // ── undo ─────────────────────────────────────────────────────────────────

  undo: () => {
    const { undoStack, redoStack, isRestoring } = get()
    // Precisa de ≥ 2 entradas: estado atual + estado anterior
    if (isRestoring || undoStack.length <= 1) return null

    const current = undoStack[undoStack.length - 1]   // estado atual → vai para redoStack
    const newUndoStack = undoStack.slice(0, -1)
    const prev = newUndoStack[newUndoStack.length - 1] // estado a restaurar

    set({
      isRestoring: true,
      undoStack: newUndoStack,
      redoStack: [...redoStack, current],
    })

    // Retorna snapshot para o chamador aplicar ao diagramStore
    // (isRestoring volta para false APÓS o chamador aplicar o estado)
    const snapshotToApply = deepClone(prev.snapshot)
    set({ isRestoring: false })
    return snapshotToApply
  },

  // ── redo ─────────────────────────────────────────────────────────────────

  redo: () => {
    const { undoStack, redoStack, isRestoring } = get()
    if (isRestoring || redoStack.length === 0) return null

    const next = redoStack[redoStack.length - 1]  // entrada a reaplicar
    const newRedoStack = redoStack.slice(0, -1)

    set({
      isRestoring: true,
      undoStack: [...undoStack, next],
      redoStack: newRedoStack,
    })

    const snapshotToApply = deepClone(next.snapshot)
    set({ isRestoring: false })
    return snapshotToApply
  },

  // ── clearHistory ──────────────────────────────────────────────────────────

  clearHistory: () => set({ undoStack: [], redoStack: [], isRestoring: false }),

  // ── initHistory ───────────────────────────────────────────────────────────

  initHistory: (snapshot) =>
    set({
      undoStack: [{ label: 'init', snapshot: deepClone(snapshot) }],
      redoStack: [],
      isRestoring: false,
    }),

  // ── Drag transaction stubs ────────────────────────────────────────────────
  // No-op até que canvas seja migrado (T-21 / T-24).
  // Quando implementado, beginTransaction deve suspender commitHistory durante
  // mousemove, e endTransaction deve comitar o estado final do drag.
  beginTransaction: (label?: string) => {
    void label
  },
  endTransaction: () => undefined,

  // ── Selectors ─────────────────────────────────────────────────────────────

  canUndo: () => get().undoStack.length > 1,
  canRedo: () => get().redoStack.length > 0,
}))

// ── Reactive selectors para componentes ──────────────────────────────────────

/** Uso: const canUndo = useHistoryStore(selectCanUndo) */
export const selectCanUndo = (s: HistoryState): boolean => s.undoStack.length > 1

/** Uso: const canRedo = useHistoryStore(selectCanRedo) */
export const selectCanRedo = (s: HistoryState): boolean => s.redoStack.length > 0

/** Número de ações que podem ser desfeitas */
export const selectUndoDepth = (s: HistoryState): number =>
  Math.max(0, s.undoStack.length - 1)

/** Número de ações que podem ser refeitas */
export const selectRedoDepth = (s: HistoryState): number => s.redoStack.length
